<script lang="ts">
  import AcceptTermsModal from "@shared/svelte/Components/AcceptTermsModal.svelte";
  import EventDetailsPane from "@shared/svelte/Components/Tracking/EventDetailsPane.svelte";
  import FlashWrapper from "@shared/svelte/Components/Flash/Wrapper.svelte";
  import GoogleTranslate from "@modern/components/shared/GoogleTranslate.svelte";
  import Header from "@shared/svelte/Components/Layout/Header.svelte";
  import Layout from "@shared/svelte/Components/Layout/Layout.svelte";
  import Main from "@shared/svelte/Components/Layout/Main.svelte";
  import NewFooter from "@shared/svelte/Components/Layout/NewFooter.svelte";
  import SalesRepPreviewHeader from "@retailer/svelte/RetailerPreview/SalesRepPreviewHeader.svelte";
  import Spinner from "@shared/svelte/Components/Spinner.svelte";
  import SupplierPreviewHeader from "@retailer/svelte/RetailerPreview/SupplierPreviewHeader.svelte";
  import WindowLocationStore from "@shared/svelte/Stores/WindowLocationStore";
  import bootstrap from "@modern/services/boot/bootstrap";
  import messagesContext from "@modern/services/messages_context";
  import retailerContext from "@modern/services/retailer_context";
  import userContext from "@modern/services/user_context";
  import { HttpPromiseWithEntireResponse } from "@shared/svelte/Modules/ApiService";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { afterPageLoad, page, url, goto, beforeUrlChange, params } from "@roxi/routify";
  import { convertParamsToStateParams } from "@modern/services/angularjs_shims/state_params.js";
  import {
    isStateAllowedForRole,
    showPreviewForbiddenFlash,
  } from "@retailer/services/preview_forbidden_flash";
  import { onDestroy } from "svelte";
  import { reRouteFromOldHomepage } from "@retailer/services/page_location_helpers";
  import { shouldNotTrackAnywhere } from "@shared/services/internal_user_check_helpers";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import { tick } from "svelte";
  import { updatePageIds } from "@shared/services/event_tracking/page_id_tracking";
  import CraftAlertsWrapper from "@shared/svelte/Components/Craft/CraftAlertsWrapper.svelte";

  import { ApolloClient, InMemoryCache, HttpLink } from "@apollo/client/core";
  import { createAuth0Client } from "@auth0/auth0-spa-js";
  import { debounce } from "lodash";

  import { setClient } from "svelte-apollo";
  import * as Sentry from "@sentry/browser";
  
  const baseUrl = `${window.location.protocol}//${window.location.host}`;
  const redirectPath = window.location.pathname + window.location.search;
  const authorizationParams = {
    audience: window["C"].SPEAKEASY_AUDIENCE,
    redirect_uri: `${baseUrl}/users/frontend_callback?redirect=${encodeURIComponent(redirectPath)}`,
    scope: "openid profile email offline_access",
  };

  const auth0Client = createAuth0Client({
    domain: window["C"].AUTH0_DOMAIN,
    clientId: window["C"].AUTH0_CLIENT_ID,
    useRefreshTokens: true,
    useRefreshTokensFallback: false,
    cacheLocation: "localstorage",
    authorizationParams: authorizationParams,
  });

  // Handle Auth0 redirect callback if code and state params are present

  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  const state = urlParams.get('state');
  
  if (code && state) {
    (async () => {
      const client = await auth0Client;
      await client.handleRedirectCallback();
      
      // Clean up URL by removing code and state params
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('code');
      cleanUrl.searchParams.delete('state');
      window.history.replaceState({}, document.title, cleanUrl.toString());
    })();
  }
  
  let proviTokenPromise;

  // Token caching helpers
  const PROVI_TOKEN_CACHE_KEY = 'provi_token_cache';
  
  // Stats accumulation and debouncing
  let statsAccumulator = {
    requests: 0,
    valid_auth0_tokens: 0
  };
  
  const STATS_DEBOUNCE_MS = 500;
  
  const sendAccumulatedStats = () => {
    if (statsAccumulator.requests === 0) {
      return; // Nothing to send
    }
    
    const statsToSend = { ...statsAccumulator };
    
    HttpPromiseWithEntireResponse({
      url: `/api/token/stats`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      data: statsToSend,
    });
    
    // Reset accumulator after sending
    statsAccumulator = {
      requests: 0,
      valid_auth0_tokens: 0
    };
  };
  
  const debouncedSendStats = debounce(sendAccumulatedStats, STATS_DEBOUNCE_MS);
  
  const getCachedToken = () => {
    try {
      const cached = sessionStorage.getItem(PROVI_TOKEN_CACHE_KEY);
      if (!cached) return null;
      
      const { token, expiresAt } = JSON.parse(cached);
      const now = Date.now();
      const fiveMinutesFromNow = now + (5 * 60 * 1000); // 5 minutes in milliseconds
      
      // Return null if token expires within the next 5 minutes
      if (expiresAt <= fiveMinutesFromNow) {
        sessionStorage.removeItem(PROVI_TOKEN_CACHE_KEY);
        return null;
      }
      
      return token;
    } catch (e) {
      // If there's any error parsing the cached token, remove it
      sessionStorage.removeItem(PROVI_TOKEN_CACHE_KEY);
      return null;
    }
  };
  
  const cacheToken = (token) => {
    try {
      // Decode JWT to get expiration time
      let expiresAt;
      try {
        // JWT structure: header.payload.signature
        const jwtParts = token.split('.');
        if (jwtParts.length !== 3) {
          throw new Error('Invalid JWT format');
        }
        
        // Decode the payload (middle part) from base64
        const base64Payload = jwtParts[1];
        const decodedPayload = atob(base64Payload);
        
        // Parse the decoded payload as JSON
        const payload = JSON.parse(decodedPayload);
        
        // JWT exp claim is in seconds, convert to milliseconds
        expiresAt = payload.exp ? payload.exp * 1000 : Date.now() + (60 * 60 * 1000); // Default 1 hour
      } catch (e) {
        // If JWT decoding/parsing fails, default to 1 hour
        console.warn('Failed to decode JWT token, using default expiration:', e);
        expiresAt = Date.now() + (60 * 60 * 1000);
      }
      
      const cacheData = {
        token,
        expiresAt
      };
      
      sessionStorage.setItem(PROVI_TOKEN_CACHE_KEY, JSON.stringify(cacheData));
    } catch (e) {
      console.warn('Failed to cache Provi token:', e);
    }
  };

  const client = new ApolloClient({
    link: new HttpLink({
      uri: () => {
        let isProxyRollout = stagedRolloutCheck("marketplace__proxy_speakeasy_requests");
        if (isProxyRollout) {
          return `${window.location.origin}/api/retailer/speakeasy_proxy`
        } else {
          return window["C"].SPEAKEASY_URL
        }
      },
      fetch: async (uri, options) => {
        let client = await auth0Client;

        let isCustomJwtRollout = stagedRolloutCheck("marketplace__custom_jwt_for_speakeasy");
        let proviToken;

        if (isCustomJwtRollout) {
          // Check for cached token first
          proviToken = getCachedToken();
        
          if (!proviToken && !proviTokenPromise) {
            proviTokenPromise = HttpPromiseWithEntireResponse({
              url: `/api/token`,
              method: "POST",
              headers: {
                "Content-Type": "text/plain",
              },
            }).then(async (res) => {
              if (res.ok) {
                let token = await res.text();
                cacheToken(token);
                return token;
              }
            })
            
            proviTokenPromise.finally(() => {
              setTimeout(() => {
                proviTokenPromise = null;
              }, 0);
            });
          }

          if (!proviToken) {
            proviToken = await proviTokenPromise;
          }
        }

        let token;
        

        try {
          token = await client.getTokenSilently({
            authorizationParams: authorizationParams,
          });
        } catch (e) {
          if (!isCustomJwtRollout) {
            if (e.message == "Login required" || e.message == "Consent required" || e.message.match(/Missing refresh token/i)) {
              client.loginWithRedirect({
                authorizationParams: authorizationParams,
              });
            } else {
              throw e;
            }
          }
        }

        if (!options.headers) {
          options.headers = {};
        }

        if (isCustomJwtRollout) {
          options.headers["Authorization"] = `Bearer ${proviToken}`;
        } else {
          options.headers["Authorization"] = `Bearer ${token}`;
        }

        // Accumulate token stats for debounced sending
        if (token) {
          statsAccumulator.valid_auth0_tokens++;
        }
        
        // Trigger debounced stats sending
        statsAccumulator.requests++;
        debouncedSendStats();
        
        return fetch(uri, options);
      },
    }),
    cache: new InMemoryCache(),
  });

  setClient(client);

  const { trackPageView } = EventTracking;

  let allowedStateForUserRole = true;
  let showSupplierPreviewHeader = false;
  let showSalesRepPreviewHeader = false;
  let showEventDetailsPanel = false;
  let loaded = false;

  onDestroy(() => {
    messagesContext.unsubscribe();
  });

  $afterPageLoad(page => {
    WindowLocationStore.update(n => {
      n.location = window.location;
      n.stateParams = $params;
      return n;
    });
    trackPageView(true);
    localStorage.setItem("current_angular_page_name", page.path);
    doAcceptTermsModal();
    return true;
  });

  $beforeUrlChange((event, route) => {
    const user = userContext.context.user;
    if (!user) {
      return false;
    }
    if (isStateAllowedForRole(user["role"], route.path)) {
      updatePageIds();
      return true;
    } else {
      showPreviewForbiddenFlash();
      return false;
    }
  });

  const doPreviewForbiddenFlash = () => {
    if (localStorage.getItem("needToShowPreviewForbiddenFlash")) {
      showPreviewForbiddenFlash();
      localStorage.removeItem("needToShowPreviewForbiddenFlash");
    }
  };

  const load = async function () {
    updatePageIds();
    let bootstrapRes = await bootstrap({ $page, $url, $goto });
    if ($page.path === "/index") {
      reRouteFromOldHomepage(convertParamsToStateParams($params));
    }
    doPreviewForbiddenFlash();
    const user = userContext.context.user;
    if (!user) {
      return false;
    } // should only happen when pageload is interrupted.
    loaded = bootstrapRes;
    if (!isStateAllowedForRole(user["role"], $page.path)) {
      allowedStateForUserRole = false;
      localStorage.setItem("retailer_preview_redirect", "true");
      window.location.href = localStorage.getItem("current_angular_page_name") || "/";
    }
    showSupplierPreviewHeader = user["role"] == "supplier";
    showSalesRepPreviewHeader = user["role"] == "sales_rep";
    showEventDetailsPanel = userContext.isAdmin() && stagedRolloutCheck("event_tracking_details");
  };

  const doAcceptTermsModal = async function () {
    await tick();
    if (!userContext?.context?.user?.accepted_terms_at && !shouldNotTrackAnywhere()) {
      window["svelteModal"](AcceptTermsModal, {
        companyName: window["C"].COMPANY_NAME,
      });
    }
  };
</script>

{#await load()}
  <Spinner />
{:then}
  {#if loaded && allowedStateForUserRole}
    <div class="app-container">
      <Layout>
        {#if showSupplierPreviewHeader}
          <SupplierPreviewHeader />
        {/if}
        {#if showSalesRepPreviewHeader}
          <SalesRepPreviewHeader retailer={retailerContext.get()} />
        {/if}
        <Header slot="header" />
        <Main id="page-wrapper" page={$page}>
          <slot />
        </Main>

        {#if showEventDetailsPanel}
          <EventDetailsPane />
        {/if}
        <div slot="footer" class="craft">
          <NewFooter>
            <GoogleTranslate />
          </NewFooter>
        </div>
      </Layout>
      <FlashWrapper />
      <CraftAlertsWrapper />
    </div>
  {/if}
  <div id="programmatic-modal-container" />
{/await}
